import {
  carryUpConditionsByCarriedUp,
  type CarriedUp,
  type OrganisationId,
  type OrganisationStructure,
} from "@thalamos/haven-common";

import type { StructureLayout } from "./layOutStructure.js";

/** One line of the plain-words account of what the drawing shows. */
export type OrganisationMembershipSummary =
  | {
      kind: "membership";
      receiverId: OrganisationId;
      receiverName: string;
      /** Its members in the structure as read, including any the drawing groups away. */
      memberCount: number;
      carries: boolean;
    }
  | { kind: "operates"; operatorName: string; operatedName: string };

/**
 * Whether each organisation in the structure receives `what` through its
 * memberships: always, unless a setting gates it and the organisation has it
 * off.
 */
export const carriedInto = (
  structure: OrganisationStructure,
  what: CarriedUp,
): ReadonlyMap<OrganisationId, boolean> => {
  const gate = carryUpConditionsByCarriedUp[what].gatedBy;

  return new Map(
    structure.organisations.map((organisation) => [
      organisation.id,
      gate === null || organisation.settings[gate],
    ]),
  );
};

/**
 * The memberships and operating links the drawing shows, as sentences can
 * state them: one per organisation receiving memberships, in the order the
 * drawing reads top to bottom, then each operating link.
 */
export const summariseStructure = (
  structure: OrganisationStructure,
  layout: StructureLayout,
  carries: ReadonlyMap<OrganisationId, boolean>,
): OrganisationMembershipSummary[] => {
  const nameOf = new Map(
    structure.organisations.map((organisation) => [
      organisation.id,
      organisation.name,
    ]),
  );
  const memberCountOf = new Map<OrganisationId, number>();
  for (const relationship of structure.relationships) {
    if (relationship.kind === "member_of") {
      const receiver = relationship.relatedOrganisationId;
      memberCountOf.set(receiver, (memberCountOf.get(receiver) ?? 0) + 1);
    }
  }

  const receiving = new Set(
    layout.edges
      .filter((edge) => edge.kind === "member_of")
      .map((edge) => edge.receiverId),
  );
  const topDown = [...layout.cards].sort((a, b) => a.y - b.y || a.x - b.x);
  const memberships = topDown.flatMap(
    (card): OrganisationMembershipSummary[] =>
      card.kind === "organisation" && receiving.has(card.organisation.id)
        ? [
            {
              kind: "membership",
              receiverId: card.organisation.id,
              receiverName: card.organisation.name,
              memberCount: memberCountOf.get(card.organisation.id) ?? 0,
              carries: carries.get(card.organisation.id) === true,
            },
          ]
        : [],
  );

  const drawnEdges = new Set(layout.edges.map((edge) => edge.key));
  const operating = structure.relationships.flatMap(
    (relationship): OrganisationMembershipSummary[] => {
      const operatedName = nameOf.get(relationship.organisationId);
      const operatorName = nameOf.get(relationship.relatedOrganisationId);
      const drawn = drawnEdges.has(relationship.id);

      return relationship.kind === "operated_by" &&
        drawn &&
        operatedName !== undefined &&
        operatorName !== undefined
        ? [{ kind: "operates", operatorName, operatedName }]
        : [];
    },
  );

  return [...memberships, ...operating];
};
